import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Form, Button, Input, Select, notification } from 'antd'
import { registerUser } from '@actions/registration'
import queryString from 'query-string'
import { selectRegistration } from '@reducers/registration/registrationSelectors'
import { AMC, COMPANY, SOLE_PROPRIETOR, PROPERTY_DATA_COLLECTOR, USER_TYPE_LABEL_MAP } from './constants'
import { ERROR, SUCCEEDED } from '@constants/fetchStatuses'
import blueLogo from '../../assets/blue-logo.png'
import '@ant-design/compatible/assets/index.css'
import '../Platform/Shared/Setup/AccountSetup.scss'
import './Signup.scss'
import './Register.scss'

const { Option } = Select

const Register = ({ location: { search } }) => {
  const { amc, userType: urlUserType } = queryString.parse(search)

  const getUserTypeOptions = () => {
    const baseTypes = [COMPANY, SOLE_PROPRIETOR]
    // Only include PROPERTY_DATA_COLLECTOR if URL contains userType=pdc
    if (urlUserType === 'pdc') {
      baseTypes.push(PROPERTY_DATA_COLLECTOR)
    }

    return baseTypes.map((userType) => (
      <Option value={userType} key={userType}>
        {USER_TYPE_LABEL_MAP[userType]}
      </Option>
    ))
  }

  const userTypeOptions = getUserTypeOptions()
  const dispatch = useDispatch()

  // Set initial user type - default to PDC if URL contains userType=pdc, otherwise COMPANY
  const getInitialUserType = () => {
    if (urlUserType === 'pdc') {
      return PROPERTY_DATA_COLLECTOR
    }
    return COMPANY
  }

  const [userType, setUserType] = useState(getInitialUserType())
  const formRef = useRef(null)
  const registration = useSelector(selectRegistration)
  const [submitting, setSubmitting] = useState(false)
  const [inviteCreated, setInviteCreated] = useState(false)
  const [formValues, setFormValues] = useState({ user_type: getInitialUserType() })

  useEffect(() => {
    if (registration.status === SUCCEEDED) {
      setInviteCreated(true)
      setSubmitting(false)
    } else if (registration.status === ERROR) {
      const config = {
        message: registration.errorMessage,
      }
      setSubmitting(false)
      notification.error(config)
    }
  }, [registration])

  useEffect(() => {
    // Update form values when user type changes
    setFormValues((prev) => ({ ...prev, user_type: userType }))
  }, [userType])

  const areStandardFieldsValid = () => {
    const hasEmail = formValues.email && formValues.email.trim().length > 0
    const hasUserType = amc || (formValues.user_type && formValues.user_type.length > 0)
    return hasEmail && hasUserType
  }

  const handleFormValuesChange = (changedValues, allValues) => {
    setFormValues(allValues)
  }

  const handleRegister = async () => {
    try {
      const userData = await formRef.current.validateFields()
      // Manually set userType for AMC
      if (amc) {
        userData.user_type = AMC
      }
      setSubmitting(true)
      dispatch(registerUser(userData, false, true))
    } catch ({ errorFields }) {
      // Silent validation - let individual form fields show their own errors
      // No toast message needed
    }
  }

  const renderStandardLayout = () => (
    <div className="standard-layout">
      <div className="form-container">
        <div className="form-header">
          <h2>Create Your Account</h2>
          <p>Join our platform to get started</p>
        </div>
        <Form.Item
          label="Email Address"
          name="email"
          rules={[
            {
              type: 'email',
              required: true,
              message: 'Please enter a valid email address',
            },
          ]}
        >
          <Input type="email" size="large" placeholder="Enter your email" data-testid="register-email-input" />
        </Form.Item>
        {!amc && (
          <Form.Item label="User Type" name="user_type" initialValue={getInitialUserType()}>
            <Select size="large" onChange={setUserType} placeholder="Select user type" data-testid="register-user-type-select">
              {userTypeOptions}
            </Select>
          </Form.Item>
        )}
        <Button
          type="primary"
          onClick={handleRegister}
          disabled={submitting || !areStandardFieldsValid()}
          size="large"
          block
          style={{ marginTop: '32px', height: '48px', fontSize: '16px' }}
          data-testid="register-submit-button"
        >
          {submitting ? 'Creating Account...' : 'Create Account'}
        </Button>
      </div>
    </div>
  )

  const renderPDRLayout = () => (
    <div className="standard-layout">
      <div className="form-container">
        <div className="form-header">
          <h2>Create Your Account</h2>
          <p>Fill in your details to get started</p>
        </div>
        <Form.Item
          label="Email Address"
          name="email"
          rules={[
            {
              type: 'email',
              required: true,
              message: 'Please enter a valid email address',
            },
          ]}
        >
          <Input type="email" size="large" placeholder="Enter your email" data-testid="register-email-input" />
        </Form.Item>
        {!amc && (
          <Form.Item label="User Type" name="user_type" initialValue={getInitialUserType()}>
            <Select size="large" onChange={setUserType} placeholder="Select user type" data-testid="register-user-type-select">
              {userTypeOptions}
            </Select>
          </Form.Item>
        )}

        <Button
          type="primary"
          onClick={handleRegister}
          disabled={submitting || !areStandardFieldsValid()}
          size="large"
          block
          style={{ marginTop: '32px', height: '48px', fontSize: '16px' }}
          data-testid="register-submit-button"
        >
          {submitting ? 'Creating Account...' : 'Create Account'}
        </Button>
      </div>
    </div>
  )

  return (
    <div className="signupPage">
      <div className="signup-navbar">
        <img src={blueLogo} alt="Reggora Logo" />
      </div>
      <div className="signup">
        <div className="signup-header">
          <div className="signup-sub">
            Already have an account? <Link to="/login" data-testid="register-login-link">Log in</Link>
          </div>
        </div>
        <div className="signup-body">
          {inviteCreated
            ? (
            <div data-testid="register-success-message">Check your email for the registration link!</div>
              )
            : (
            <div className="modern-signup-form">
              <Form
                ref={formRef}
                validateTrigger="onSubmit"
                layout="vertical"
                onValuesChange={handleFormValuesChange}
                initialValues={{ user_type: getInitialUserType() }}
                data-testid="register-form"
              >
                {userType === PROPERTY_DATA_COLLECTOR ? renderPDRLayout() : renderStandardLayout()}
              </Form>
            </div>
              )}
        </div>
      </div>
    </div>
  )
}

export default Register
